import styles from 'css/src/components/common/DashboardProjectList.module.scss';

import React from 'react';
import { useSelector } from 'react-redux';

import DashboardProjectItem from 'js/src/components/common/DashboardProjectItem';
import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS,
    BUTTON_WIDTHS,
} from 'js/src/components/common/ui/Button';
import { getIsUserAllowedToManageProject, selectProjectIdsInWorkspace } from 'js/src/data/account';
import { navigateToDashboardPath, pathForView, selectActiveView } from 'js/src/data/dashboard';
import type { RootState } from 'js/src/libs/RootState';
import { loadAllProjectsOperation } from 'js/src/luminary/dashboard-luminary-operations';
import { useLuminaryOperations } from 'js/src/luminary/luminary-hooks';

type OwnProps = {
    onProjectAdd: VoidFunction;
};

export const DashboardProjectList: React.FC<OwnProps> = React.memo(function ProjectList({
    onProjectAdd,
}) {
    const projectIds = useSelector((state: RootState) =>
        selectProjectIdsInWorkspace(state, SERVER.USER.workspace_id),
    );
    const activeView = useSelector(selectActiveView);
    const userCanManageProjects = useSelector(getIsUserAllowedToManageProject);

    const { loadAllProjects } = useLuminaryOperations({ loadAllProjectsOperation });

    const hasLoadedProjects = React.useRef(false);
    React.useEffect(() => {
        if (hasLoadedProjects.current) return;
        hasLoadedProjects.current = true;
        loadAllProjects();
    }, [loadAllProjects]);

    const setActiveProject = React.useCallback((projectOrmId: number) => {
        navigateToDashboardPath(pathForView(projectOrmId));
    }, []);

    const projectAddButton = userCanManageProjects ? (
        <Button
            size={BUTTON_SIZES.SM}
            color={BUTTON_COLORS.TERTIARY}
            variant={BUTTON_VARIANTS.OUTLINE}
            width={BUTTON_WIDTHS.NO_MIN_WIDTH}
            text="Add"
            iconLeft="add"
            data-testid="add-project"
            onClick={onProjectAdd}
        />
    ) : null;

    const projects = projectIds.map((projectId) => {
        return (
            <DashboardProjectItem
                activeView={activeView}
                onProjectClick={setActiveProject}
                key={projectId}
                projectId={projectId}
            />
        );
    });

    return (
        <>
            <li
                className={`${styles['add-project']} d-flex justify-content-between`}
                key="add-project"
            >
                <span className="my-l5-auto fw-semibold">PROJECTS</span>
                {projectAddButton}
            </li>
            {projects}
        </>
    );
});
